import React, { useCallback, useEffect, useState } from "react";
import { AppRemixIcon, AppNoInfo } from "@/layout/AppComponents";
import styled from "styled-components";
import { Scrollbars } from "react-custom-scrollbars";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactDOMServer from "react-dom/server";
import { axiosPath } from "../../../../../../routing/helpers/router";
import colors from "../../../../../../constants/colors";
import * as currentPageAction from "../../../../../../store/actions/currentPageAction";
import Translator from "../../../../../components/helpers/Translator";
import CorrectiveActionsTaskUpdateAdd from "./CorrectiveActions/CorrectiveActionsTaskUpdateAdd";
import CorrectiveActionsTaskUpdateHome from "./CorrectiveActions/CorrectiveActionsTaskUpdateHome";
import CorrectiveActionsTaskUpdateEdit from "./CorrectiveActions/CorrectiveActionsTaskUpdateEdit";
import SpinnerLoader from "../../../../../../layout/components/helpers/SpinnerLoader";

const CorrectiveActionsTab = (props) => {
  const { taskID } = props;
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const taskUpdates = useSelector(
    (s) => s.currentPage?.taskUpdatesTab?.[taskID]
  );
  const modulePermissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.permissions
  );
  const userBridgePermissions = useSelector(
    (s) => s.module[moduleBridgeHex].userBridge?.permissions
  );
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("empty");
  const [selectedTaskUpdate, setSelectedTaskUpdate] = useState(null);

  useEffect(() => {
    if (taskUpdates === undefined) setIsLoading(true);
  }, []);

  // > LOAD CORRECTIVE ACTIONS TAB
  const loadTaskUpdates = useCallback(() => {
    setIsLoading(true);
    axios
      .post(axiosPath("api_r_user_module_correctiveActions_taskUpdate_list"), {
        moduleBridgeHex,
        taskID,
      })
      .then((r) => {
        dispatch(
          currentPageAction.change("taskUpdatesTab", {
            [taskID]: r.data?.taskUpdates,
          })
        );
        setIsLoading(false);
      });
  }, [moduleBridgeHex, taskID]);

  useEffect(() => {
    loadTaskUpdates();
  }, []);

  useEffect(() => {
    if (selectedTaskUpdate)
      setSelectedTaskUpdate(
        taskUpdates.find((tu) => tu?.id === selectedTaskUpdate?.id)
      );
  }, [taskUpdates]);
  // < LOAD CORRECTIVE ACTIONS TAB

  return (
    <CorrectiveActionsTaskContainer>
      <SidebarMenu
        $taskUpdateClosedSystem={
          modulePermissions?.correctiveActions?.taskUpdateClosedSystem
        }
      >
        {(userBridgePermissions?.level === 0 ||
          userBridgePermissions?.addActionToOwnTask === true) && (
          <BtnAdd
            $selected={viewMode === "add"}
            onClick={() => {
              if (isLoading) return;
              setViewMode("add");
              setSelectedTaskUpdate(undefined);
            }}
          >
            <Translator file="module-legalCompliance" name="add.action" />
          </BtnAdd>
        )}
        {isLoading ? (
          <AppNoInfo color={colors.primaryLight}>
            <SpinnerLoader />
          </AppNoInfo>
        ) : (
          <>
            {taskUpdates?.length > 0 ? (
              <Scrollbars style={{ height: "360px" }}>
                <ul>
                  {taskUpdates.map((taskUpdate) => (
                    <li
                      key={taskUpdate?.id}
                      style={
                        modulePermissions?.correctiveActions
                          ?.taskUpdateClosedSystem
                          ? {
                              borderLeftColor: taskUpdate?.closedStamp
                                ? colors.success
                                : taskUpdate.status === "danger"
                                ? colors.danger
                                : taskUpdate.status === "warning"
                                ? colors.state2
                                : colors.warning,
                            }
                          : null
                      }
                      className={
                        selectedTaskUpdate?.id === taskUpdate?.id
                          ? "ev-selected"
                          : ""
                      }
                      onClick={() => {
                        setSelectedTaskUpdate(taskUpdate);
                        setViewMode("home");
                      }}
                    >
                      <div className="first-row">
                        <span className="name" title={taskUpdate?.date}>
                          {taskUpdate?.date}
                        </span>
                        {modulePermissions?.correctiveActions
                          ?.taskUpdateClosedSystem &&
                          taskUpdate?.closedStamp && (
                            <span>{taskUpdate?.closedStamp}</span>
                          )}
                      </div>
                      <div className="second-row">
                        <span className="sr-date">
                          {taskUpdate?.type?.name}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </Scrollbars>
            ) : (
              <AppNoInfo
                text={ReactDOMServer.renderToString(
                  <Translator
                    file="module-legalCompliance"
                    name="without.actions"
                  />
                )}
                color={colors.primaryLight}
                icon={
                  <AppRemixIcon
                    size={24}
                    icon="file-search"
                    color={colors.primaryLight}
                  />
                }
              />
            )}
          </>
        )}
      </SidebarMenu>
      <ContentTask>
        {isLoading ? (
          <AppNoInfo color={colors.primaryLight}>
            <SpinnerLoader />
          </AppNoInfo>
        ) : (
          <>
            {viewMode === "add" && (
              <CorrectiveActionsTaskUpdateAdd
                onCreate={() => {
                  loadTaskUpdates();
                  setViewMode("empty");
                }}
                onCancel={() => setViewMode("empty")}
                modulePermissions={modulePermissions}
                taskID={taskID}
              />
            )}
            {viewMode === "edit" && (
              <CorrectiveActionsTaskUpdateEdit
                taskID={taskID}
                selectedTaskUpdate={selectedTaskUpdate}
                onEdit={() => {
                  loadTaskUpdates();
                  setViewMode("home");
                }}
                onDelete={() => {
                  setSelectedTaskUpdate(undefined);
                  loadTaskUpdates();
                  setViewMode("empty");
                }}
                onCancel={() => setViewMode("home")}
                modulePermissions={modulePermissions}
              />
            )}
            {viewMode === "home" && (
              <CorrectiveActionsTaskUpdateHome
                taskID={taskID}
                selectedTaskUpdate={selectedTaskUpdate}
                onEdit={() => {
                  setSelectedTaskUpdate(selectedTaskUpdate);
                  setViewMode("edit");
                }}
                onClose={() => {
                  setViewMode("empty");
                  setSelectedTaskUpdate(undefined);
                }}
                modulePermissions={modulePermissions}
                onToggleClosed={loadTaskUpdates}
                onUpdateClosedStamp={loadTaskUpdates}
              />
            )}
            {viewMode === "empty" && (
              <AppNoInfo
                text={
                  <span>
                    <Translator
                      file="module-correctiveActions"
                      name="select.task.update.list.create.button.new.task.update"
                    />
                  </span>
                }
                color={colors.primaryLight}
                icon={
                  <AppRemixIcon
                    size={40}
                    icon="calendar-check"
                    color={colors.primaryLight}
                  />
                }
              />
            )}
          </>
        )}
      </ContentTask>
    </CorrectiveActionsTaskContainer>
  );
};
export default CorrectiveActionsTab;

const CorrectiveActionsTaskContainer = styled.div`
  height: 450px;
  display: grid;
  grid-template-columns: 260px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  ul {
    padding: 4px;
    overflow-y: auto;
    li {
      border-left-width: ${(props) =>
        props.$taskUpdateClosedSystem ? "6px" : "0px"};
      border-left-style: solid;
      padding: 4px 6px 4px 4px;
      border-bottom: 1px solid ${colors.grey300};
      cursor: pointer;
      &.ev-selected {
        background: ${colors.grey300};
      }
      .first-row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        color: ${colors.primary};
        .name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .second-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        .sr-date {
          letter-spacing: 1px;
        }
        .sr-user {
          color: ${colors.primary};
        }
      }
    }
  }
`;

const BtnAdd = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${colors.primary};
  background-color: ${(props) =>
    props.$selected ? colors.primary : "initial"};
  color: ${(props) => (props.$selected ? "#fff" : colors.primary)};
  margin: 0;
  height: 32px;
  cursor: pointer;
`;

const ContentTask = styled.div`
  overflow-y: auto;
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  .row-no-margin > .row {
    margin: 0;
  }
`;
